import PropTypes from "prop-types";
import {ToggleButtonGroup} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import makeStyles from "@mui/styles/makeStyles";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {isNull} from "lodash";
import Tooltip from '@mui/material/Tooltip';

const useSwitchStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.designSystem.palette.neutral0,
        // backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#393939",
        width: "fit-content",
        borderRadius: 5,
        "&.padding": {
            padding: 3,
        },
    },
    label: {
        fontFamily: "Inter",
        fontWeight: 400,
        textTransform: "none",
        color: theme.palette.mode === "light" ? "#2a2a2a" : "#929292",
        border: "none",
        minWidth: 40,
        "&:hover": {
            color: theme.palette.mode === "light" ? "#000000" : "#ffffff",
        },
        "&.small": {
            height: 30,
        },
        "&.medium": {
            height: 35,
        },
        "&.large": {
            height: 40,
        },
        '&.Mui-disabled': {
            border: "none",
        }
    },
    selected: {
        "&&": {
            backgroundColor: theme.palette.designSystem.palette.dark1,
            color: theme.palette.designSystem.palette.text1,
            borderRadius: 5,
            "&:hover": {
                backgroundColor: theme.palette.designSystem.palette.light1,
                color: theme.palette.designSystem.palette.text1,
            },
        },
        '&.Mui-disabled': {
            backgroundColor:  "#929292",
            border: "none",
        }
    },
    block: {
        padding: 4
    }
}));

const AgyleBlockSwitch = (props) => {
    const classes = useSwitchStyles();
    const {t} = useTranslation();

    let labels = {
        yes: t("Yes"),
        no: t("No"),
        empty: t("N/R"),
        alternative: t("Alternative"),
    };
    if (props.customLabels) {
        labels = {
            ...labels,
            ...props.customLabels,
        };
    }

    const handleChange = (e, value) => {
        if (typeof value !== "object") {
            let val = value;
            if (val === "null") val = null;
            props.onChange(val, e);
        }
    };

    const getVal = () => {
        return isNull(props.value) ? "null" : props.value;
    };

    const buttonTrue = () => (
      <ToggleButton
        className={clsx(
          classes.label,
          props.size,
          props.customClass,
        )}
        classes={{selected: classes.selected}}
        sx={{
            "&.MuiToggleButtonGroup-grouped": {
                borderRadius: "5px !important",
            },
        }}
        value={props.valueObject.true}
        disabled={props?.disabledState?.yes}
      >
          {labels.yes}
      </ToggleButton>
    )

    const buttonNullState = () => (
      <ToggleButton
        className={clsx(
          classes.label,
          props.size,
          props.customClass,
        )}
        classes={{selected: classes.selected}}
        sx={{
            "&.MuiToggleButtonGroup-grouped": {
                borderRadius: "5px !important",
            },
        }}
        value={props.valueObject.null || "null"} // Must be as string, ToggleButton cannot have Nullable value
        disabled={props?.disabledState?.null}
      >
          {labels.empty}
      </ToggleButton>
    );

    const buttonAlternative = () => (
            <ToggleButton
              className={clsx(
                classes.label,
                props.size,
                props.customClass,
              )}
              classes={{selected: classes.selected}}
              sx={{
                  "&.MuiToggleButtonGroup-grouped": {
                      borderRadius: "5px !important",
                  },
              }}
              value={props.valueObject.alt || "alt"} // Must be as string, ToggleButton cannot have Nullable value
            >
                {labels.alternative}
            </ToggleButton>
    )

    const buttonFalse = () => (
        <ToggleButton
          className={clsx(
            classes?.label,
            props?.size,
            props.customClass,
          )}
          classes={{selected: classes?.selected}}
          sx={{
              "&.MuiToggleButtonGroup-grouped": {
                  borderRadius: "5px !important",
              },
          }}
          value={props?.valueObject?.false}
          disabled={props?.disabledState?.no}
        >
            {labels?.no}
        </ToggleButton>
    )

    return (
        <div
            className={clsx(classes.root, props.padding && "padding")}
            style={{...props.style}}
        >
            <ToggleButtonGroup
                value={getVal()}
                exclusive={!props.multiSelect}
                onChange={handleChange}
                orientation={props.orientation}
                size={props.size}
                disabled={props.disabled}
                className={clsx(!props.disableBlockPadding && classes.block)}
            >
                {props?.tooltipObject?.true ?
                    <Tooltip title={props?.tooltipObject?.true} placement="bottom" value={props.valueObject?.true}>
                        {buttonTrue()}
                    </Tooltip> :
                    buttonTrue()
                }

                {props.nullState && (
                  props?.tooltipObject?.null ?
                    <Tooltip title={props?.tooltipObject?.null} placement="bottom" value={props.valueObject?.null}>
                        {buttonNullState()}
                    </Tooltip> :
                    buttonNullState()
                )}

                {props.fourCases && (
                  props?.tooltipObject?.alt ?
                    <Tooltip title={props?.tooltipObject?.alt} placement="bottom" value={props.valueObject?.alt}>
                        {buttonAlternative()}
                    </Tooltip> :
                    buttonAlternative()
                )}

                {props?.tooltipObject?.no ?
                  <Tooltip title={props?.tooltipObject?.no} placement="bottom" value={props.valueObject?.false}>
                      {buttonFalse()}
                  </Tooltip> :
                  buttonFalse()
                }
            </ToggleButtonGroup>
        </div>
    );
};

export default AgyleBlockSwitch;

AgyleBlockSwitch.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    orientation: PropTypes.oneOf(["horizontal", "vertical"]),
    size: PropTypes.oneOf(["small", "medium", "large"]),
    nullState: PropTypes.bool,
    customLabels: PropTypes.object,
    multiSelect: PropTypes.bool,
    padding: PropTypes.bool,
    valueObject: PropTypes.object,
    disabledState: PropTypes.object,
    customClass: PropTypes.oneOfType(PropTypes.object, PropTypes.string),
    disableBlockPadding: PropTypes.bool,
};

AgyleBlockSwitch.defaultProps = {
    value: false,
    disabled: false,
    nullState: false,
    multiSelect: false,
    disableBlockPadding: false,
    orientation: "horizontal",
    size: "small",
    customLabels: null,
    valueObject: {
        false: false,
        true: true,
    },
    disabledState: {
        no: false,
        yes: false,
        null: false
    }
};